import type { OrgRole } from "./member-change-copy";

export type OrganizationMember = {
  _id: string;
  name?: string;
  email: string;
  orgRole: string;
  /** When they last joined this organization. */
  joinedAt?: number;
};

export type InvitationState = "pending" | "accepted" | "expired" | "revoked";

export type OrganizationInvitation = {
  id: string;
  email: string;
  state: InvitationState;
  expiresAt: string;
  createdAt: string;
  isReviewer: boolean;
  role: Exclude<OrgRole, "owner">;
};

export type MemberStatus = "active" | "pending" | "expired" | "revoked";

/** One person in the members table: an active member or an invitation that hasn't been accepted. */
export type MemberRow =
  | {
      kind: "member";
      id: string;
      name: string | null;
      email: string;
      role: string;
      status: "active";
      /** When they joined, if known. */
      date: number | null;
      member: OrganizationMember;
    }
  | {
      kind: "invitation";
      id: string;
      name: null;
      email: string;
      role: string;
      status: Exclude<MemberStatus, "active">;
      /** When they were invited. */
      date: number | null;
      invitation: OrganizationInvitation;
    };

const ROLE_ORDER: Record<string, number> = { owner: 0, admin: 1, member: 2, reviewer: 3 };
const STATUS_ORDER: Record<MemberStatus, number> = { active: 0, pending: 1, expired: 2, revoked: 3 };

export function roleRank(role: string) {
  return ROLE_ORDER[role] ?? Object.keys(ROLE_ORDER).length;
}

export function statusRank(status: MemberStatus) {
  return STATUS_ORDER[status];
}

function parseDate(value: string) {
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

/**
 * Members and open invitations as one list. Accepted invitations are dropped: the person is
 * already listed as a member.
 */
export function buildMemberRows(
  members: OrganizationMember[],
  invitations: OrganizationInvitation[]
): MemberRow[] {
  const memberRows: MemberRow[] = members.map((member) => ({
    kind: "member",
    id: member._id,
    name: member.name?.trim() || null,
    email: member.email,
    role: member.orgRole,
    status: "active",
    date: member.joinedAt ?? null,
    member,
  }));
  const invitationRows: MemberRow[] = invitations
    .filter((invitation) => invitation.state !== "accepted")
    .map((invitation) => ({
      kind: "invitation",
      id: invitation.id,
      name: null,
      email: invitation.email,
      role: invitation.role,
      status: invitation.state as Exclude<MemberStatus, "active">,
      date: parseDate(invitation.createdAt),
      invitation,
    }));
  return [...memberRows, ...invitationRows];
}

export function rowDisplayName(row: MemberRow) {
  return row.name ?? row.email;
}
